import {
  Component, Inject, OnDestroy, OnInit
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogRef,
} from '@angular/material/dialog';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { CoursesService } from 'src/app/services/courses.service';
import { ToastService } from 'src/app/services/toast.service';
import { Subscription } from 'rxjs';                    
import { AuthService } from 'src/app/services/auth.service'; 
import { firstValueFrom } from 'rxjs';


type PermissionMap = Record<string, number>;
type ValidationKind = 'HONOR' | 'ADMIN' | 'OTHER';

interface StatusOption {
  value: 'REGISTERED' | 'COMPLETED' | 'NOT_TAKEN' | 'TAKE_SURVEY' | 'UNREGISTERED';
  label: string;
}

@Component({
  selector: 'app-update-status-modal',
  templateUrl: './update-status-modal.component.html',
  styleUrls: [ './update-status-modal.component.scss' ],
})
export class UpdateStatusModalComponent implements OnInit, OnDestroy {
  status: any;
  courseRegistrationSubscription: any;
  private permissionsSub?: Subscription;              
  loggedInUserPermission: PermissionMap | null = null;
  loggedInUser: any = null;
  courses: any;

  hasValidateAttendance = false;
  validationKind: ValidationKind = 'OTHER';
  statusOptions: StatusOption[] = [];

  private subs: Array<{ unsubscribe: () => void }> = [];



  constructor(
    private coursesService: CoursesService,
    private toastService: ToastService,
    public dialogRef: MatDialogRef<UpdateStatusModalComponent>,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    const permSub = this.authService.permissions$.subscribe((perms: PermissionMap) => {
      this.loggedInUserPermission = perms;

      const va = perms?.['VALIDATE_ATTENDANCE'] ?? 0;
      this.hasValidateAttendance = Number(va) === 1;

      this.buildStatusOptions();
    });
    this.subs.push(permSub);

    const userSub = this.authService.user$.subscribe((user: any) => {
      this.loggedInUser = user;
    });
    this.subs.push(userSub);

    const rawValidation =
      this.data?.validationKid ??
      this.data?.validationKind ??
      this.data?.course?.validationKid ??
      this.data?.course?.validationKind ??
      this.data?.pdValidation ??
      '';

    const v = (String(rawValidation || '').toUpperCase() as ValidationKind) || 'OTHER';
    this.validationKind = v === 'HONOR' ? 'HONOR' : v === 'ADMIN' ? 'ADMIN' : 'OTHER';
    console.log('[UpdateStatusModal] validationKind:', this.validationKind);

    // (in case permissions arrive late, this SHOULD rebuild in the permSub)
    this.buildStatusOptions();
  }

  checkCanComplete = (data: any) => {
    const {
      validationKid, sessionEndDate, dateOffered,
    } = data;

    if (validationKid !== 'HONOR') {
      return false;
    }

    const asyncRegex = new RegExp('async', 'i');
    if (dateOffered.match(asyncRegex)) {
      return true;
    }

    const date = new Date(dateOffered);
    if (date instanceof Date && !isNaN(date.getTime())) {
      return new Date() > new Date(sessionEndDate);
    }

    return false;
  }

  ngOnDestroy(): void {
    if (this.permissionsSub) this.permissionsSub.unsubscribe();
    if(this.courseRegistrationSubscription) {
      this.courseRegistrationSubscription.unsubscribe();
    }
  }

  private buildStatusOptions(): void {
    let options: StatusOption[] = [];

    if (this.hasValidateAttendance) {
      options = [
        { value: 'REGISTERED',  label: 'Registered' },
        { value: 'COMPLETED',   label: 'Complete' },
        { value: 'NOT_TAKEN',      label: 'Missed' },
        { value: 'TAKE_SURVEY', label: 'Take Survey' },
        { value: 'UNREGISTERED', label: 'Remove Course' },
      ];
    } else {
      if (this.validationKind === 'HONOR') {
        //  Honor validation, no permission
        options = [
          { value: 'COMPLETED',     label: 'I have taken this course' },
          { value: 'NOT_TAKEN',        label: 'I did not take this course' },
          { value: 'UNREGISTERED', label: 'Cancel my registration' },
        ];
      } else if (this.validationKind === 'ADMIN') {
        //  Admin validation, no permission
        options = [
          { value: 'UNREGISTERED', label: 'Cancel my registration' },
        ];
      } else {
        options = [
          { value: 'REGISTERED',  label: 'Registered' },
          { value: 'COMPLETED',   label: 'Complete' },
          { value: 'NOT_TAKEN',      label: 'Missed' },
          { value: 'TAKE_SURVEY', label: 'Take Survey' },
          { value: 'UNREGISTERED', label: 'Remove Course' },
        ];
      }
    }

    this.statusOptions = options;
    if (this.status && !this.statusOptions.find(o => o.value === this.status)) {
      this.status = null;
    }
  }

  closeStatusModal(): void {
    this.dialogRef.close();
  }

async submitStatus() {
  let statusKid: 'REGISTERED'|'COMPLETED'|'NOT_TAKEN'|'UNREGISTERED' |'TAKE_SURVEY';
  switch (this.status) {
    case 'completed':
    case 'COMPLETED':
      statusKid = 'COMPLETED';
      break;
    case 'not-taken':
      statusKid = 'NOT_TAKEN';
      break;
    case 'UNREGISTERED':
      statusKid = 'UNREGISTERED';
      break;
    case 'NOT_TAKEN':
      statusKid = 'NOT_TAKEN';
      break;
    case 'TAKE_SURVEY':
      statusKid = 'TAKE_SURVEY';
      break;
    default:
      statusKid = 'REGISTERED';
      break;
  }

  const updateStatusData = { id: this.data.id, statusKid };

  // 2) Log so you can see the click actually fires
  console.log('[UpdateStatusModal] submitting status', updateStatusData);

  try {
    // 3) Use firstValueFrom so this request is NOT tied to a stored subscription
    const resp = await firstValueFrom(
      this.coursesService.updateCourseRegistration(updateStatusData)
    );

    console.log('[UpdateStatusModal] success:', resp);
    this.toastService.setToast({ text: 'Status Updated', type: 'success' });

    // 4) Close dialog after success
    this.dialogRef.close(true);
  } catch (error: any) {
    console.error('[UpdateStatusModal] error:', error);
    this.dialog.open(VerificationComponent, {
      data: {
        type: 'alert',
        title: 'Oops, something went wrong updating the status',
        text: error?.error?.message ?? 'Unknown error',
      },
    });
  }
}

  get isHonorValidation(): boolean { return this.validationKind === 'HONOR'; }
  get isAdminValidation(): boolean { return this.validationKind === 'ADMIN'; }
}
